/**
 * Упоминание участника через `@`.
 *
 * Живёт в приложении, а не в дизайн-системе: список кандидатов берётся из API
 * трекера, а бэкенду нужны идентификаторы упомянутых отдельным полем
 * (`mentioned_user_ids`) — markdown он не разбирает.
 *
 * В markdown упоминание уезжает обычной ссылкой `[@Имя](user:UUID)`. Так оно
 * читается и без трекера, а при следующем открытии редактор собирает из неё
 * прежний узел — иначе правка комментария теряла бы всех упомянутых.
 */
import Mention from '@tiptap/extension-mention'
import type { MarkdownSerializerState } from 'prosemirror-markdown'
import type { Node as ProseMirrorNode } from 'prosemirror-model'

import { renderMentionList, type MentionUser } from './mention-list'

export type { MentionUser }

/** Схема ссылки, под которой упоминание живёт в markdown */
const USER_HREF = 'user:'

const attrs = (node: ProseMirrorNode) => ({
  id: String(node.attrs.id ?? ''),
  label: String(node.attrs.label ?? node.attrs.id ?? ''),
})

export const createMentionExtension = (search: (query: string) => Promise<MentionUser[]>) =>
  Mention.extend({
    addStorage() {
      return {
        markdown: {
          serialize(state: MarkdownSerializerState, node: ProseMirrorNode) {
            const { id, label } = attrs(node)

            state.write(`[@${label}](${USER_HREF}${id})`)
          },
          parse: {
            updateDOM(element: HTMLElement) {
              element.querySelectorAll(`a[href^="${USER_HREF}"]`).forEach((link) => {
                const id = link.getAttribute('href')?.slice(USER_HREF.length) ?? ''
                const label = (link.textContent ?? '').replace(/^@/, '')
                const mention = document.createElement('span')

                mention.setAttribute('data-type', 'mention')
                mention.setAttribute('data-id', id)
                mention.setAttribute('data-label', label)
                link.replaceWith(mention)
              })
            },
          },
        },
      }
    },
  }).configure({
    HTMLAttributes: { class: 'tracker-mention' },
    // В тексте (уведомления, поиск, копирование) упоминание должно читаться
    // так же, как выглядит на экране.
    renderText: ({ node }) => `@${attrs(node).label}`,
    suggestion: {
      char: '@',
      items: ({ query }: { query: string }) => search(query),
      render: renderMentionList,
    },
  })
